/**
 * Les events temps réel arrivent soit à l'unité (`live-event`, `live-reaction`...), soit groupés
 * dans un `batch-events` que le back publie depuis son buffer Redis. Ce buffer peut republier un
 * lot déjà envoyé (bail Redis expiré pendant une publication Ably lente) : chaque event porte donc
 * un `eventId` stable qui, lui, survit à la republication — l'id du message Ably, non, il change à
 * chaque publication et ne permet aucun dédoublonnage.
 */

export interface NormalizedLiveMessage {
  eventId?: string;
  name: string;
  data: unknown;
}

const hasEventName = (event: unknown): event is { name: string; data?: unknown } =>
  !!event && typeof event === 'object' && typeof (event as { name?: unknown }).name === 'string';

export const readLiveEventId = (event: unknown): string | undefined => {
  if (!event || typeof event !== 'object') {
    return undefined;
  }
  const eventId = (event as { eventId?: unknown }).eventId;
  return typeof eventId === 'string' && eventId.length > 0 ? eventId : undefined;
};

export const normalizeLiveBatchMessage = (
  message: { name?: string; data?: unknown } | null | undefined,
): NormalizedLiveMessage[] => {
  if (!message) {
    return [];
  }

  if (message.name === 'batch-events' && Array.isArray(message.data)) {
    return message.data.filter(hasEventName).map((event) => ({
      eventId: readLiveEventId(event),
      name: event.name,
      data: event.data,
    }));
  }

  if (typeof message.name !== 'string') {
    return [];
  }

  return [{ name: message.name, data: message.data }];
};

export interface LiveEventDeduplicator {
  shouldProcess: (eventId?: string) => boolean;
}

const DEFAULT_DEDUPLICATION_WINDOW = 500;

/**
 * Fenêtre FIFO bornée : un live dure plusieurs heures, on ne peut pas garder tous les ids. Un event
 * sans `eventId` passe toujours — le back ne l'émet pas encore partout, et on préfère un doublon
 * résiduel à un event avalé.
 */
export const createLiveEventDeduplicator = (
  windowSize: number = DEFAULT_DEDUPLICATION_WINDOW,
): LiveEventDeduplicator => {
  const seenEventIds = new Set<string>();
  const seenOrder: string[] = [];

  return {
    shouldProcess: (eventId?: string) => {
      if (!eventId) {
        return true;
      }
      if (seenEventIds.has(eventId)) {
        return false;
      }
      seenEventIds.add(eventId);
      seenOrder.push(eventId);
      while (seenOrder.length > windowSize) {
        const evictedEventId = seenOrder.shift();
        if (evictedEventId !== undefined) {
          seenEventIds.delete(evictedEventId);
        }
      }
      return true;
    },
  };
};

/**
 * Des milliers de spectateurs reçoivent le même `RESOURCE_SHOW` à l'instant près : sans étalement,
 * ils refetchent tous `/resources/state` simultanément. 500 ms reste imperceptible face au discours
 * du présentateur (même valeur que le resync au retour au premier plan, cf. AttendeeLivePage).
 */
export const RESOURCE_STATE_REFETCH_JITTER_MS = 500;

export const resourceStateRefetchDelayMs = (): number =>
  Math.random() * RESOURCE_STATE_REFETCH_JITTER_MS;
